import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Users, Building, Download, ChevronDown, PanelLeft } from 'lucide-react';

import LeadsTable from './LeadsTable';
import ChatWidget from './ChatWidget';
import AppHeader from './AppHeader';

import { getLeadsByEnterprise, deleteLead } from '../services/dataService';
import { getApiBaseUrl } from '../services/api';
import { authService } from '../services/authService';
import { leadEventBus } from '../services/leadEventBus';

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultStartDate = () => {
  const today = new Date();
  return formatDateForInput(new Date(today.getFullYear(), today.getMonth() - 1, today.getDate()));
};

const getDefaultEndDate = () => formatDateForInput(new Date());
const GIDashboard = ({ user, onLogout, unreadLeadIds, setUnreadLeadIds, defaultEnterprise }) => {
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      return localStorage.getItem('giDashboardSidebarOpen') !== 'false';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('giDashboardSidebarOpen', String(sidebarOpen));
    } catch {
      /* storage unavailable - not critical */
    }
  }, [sidebarOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        setSidebarOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const [enterprises, setEnterprises] = useState([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [loadingEnterprises, setLoadingEnterprises] = useState(false);

  const [stores, setStores] = useState([]);
  const [selectedStoreFilter, setSelectedStoreFilter] = useState(null);

  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState('lastMessage');
  const [sortDirection, setSortDirection] = useState('desc');
  const [bodyTypeFilter, setBodyTypeFilter] = useState('');
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [searchQuery, setSearchQuery] = useState('');

  const [openLeadIds, setOpenLeadIds] = useState([]);
  const [lastOpenedLeadId, setLastOpenedLeadId] = useState(null);
  const [widgetWidths, setWidgetWidths] = useState({}); // { leadId: px }
  const [focusedLeadId, setFocusedLeadId] = useState(null);
  const [scrollToBottomOnLoad, setScrollToBottomOnLoad] = useState(false);
  const [deletingLeadId, setDeletingLeadId] = useState(null);
  const [cancellingAppointmentId, setCancellingAppointmentId] = useState(null);

  const openLeadsCacheRef = useRef(new Map());
  openLeadIds.forEach((id) => {
    const fresh = leads.find((lead) => lead.id === id);
    if (fresh) openLeadsCacheRef.current.set(id, fresh);
  });
  Array.from(openLeadsCacheRef.current.keys()).forEach((id) => {
    if (!openLeadIds.includes(id)) openLeadsCacheRef.current.delete(id);
  });

  const openLeads = openLeadIds
    .map((id) => leads.find((lead) => lead.id === id) || openLeadsCacheRef.current.get(id))
    .filter(Boolean);

  const WIDGET_GAP = 12;
  const DEFAULT_WIDGET_WIDTH = 440;
  const widgetRightOffset = (index) =>
    openLeads
      .slice(0, index)
      .reduce((sum, lead) => sum + (widgetWidths[lead.id] || DEFAULT_WIDGET_WIDTH) + WIDGET_GAP, 0);

  const openLead = useCallback((lead) => {
    if (!lead || !lead.id) return;
    setOpenLeadIds((prev) => {
      if (prev.includes(lead.id)) return prev;
      const next = [...prev, lead.id];
      return next.length > 3 ? next.slice(next.length - 3) : next;
    });
    setLastOpenedLeadId(lead.id);
    setFocusedLeadId(lead.id);
    setScrollToBottomOnLoad(true);
    setTimeout(() => setScrollToBottomOnLoad(false), 100);
  }, []);

  const closeLead = useCallback((leadId) => {
    setOpenLeadIds((prev) => prev.filter((id) => id !== leadId));
    setWidgetWidths((prev) => {
      if (!(leadId in prev)) return prev;
      const next = { ...prev };
      delete next[leadId];
      return next;
    });
  }, []);

  const handleWidgetWidthChange = useCallback((leadId, width) => {
    setWidgetWidths((prev) => {
      if (width == null) {
        if (!(leadId in prev)) return prev;
        const next = { ...prev };
        delete next[leadId];
        return next;
      }
      if (prev[leadId] === width) return prev;
      return { ...prev, [leadId]: width };
    });
  }, []);

  const loadLeadsRef = useRef(null);
  const openLeadIdsRef = useRef([]);
  const selectedEnterpriseRef = useRef(null);
  const currentLoadControllerRef = useRef(null);
  const pageRef = useRef(0);

  useEffect(() => {
    openLeadIdsRef.current = openLeadIds;
  }, [openLeadIds]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    selectedEnterpriseRef.current = selectedEnterprise;
  }, [selectedEnterprise]);

  useEffect(() => {
    return () => {
      currentLoadControllerRef.current?.abort();
      currentLoadControllerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const loadEnterprises = async () => {
      setLoadingEnterprises(true);
      setError('');
      try {
        const response = await authService.apiCall('/admin/enterprises');
        if (response.ok) {
          const enterprisesData = await response.json();
          setEnterprises(enterprisesData);
          // Set default enterprise if provided and valid
          if (defaultEnterprise && enterprisesData.find(e => e.id === defaultEnterprise)) {
            setSelectedEnterprise(defaultEnterprise);
          }
        }
      } catch (err) {
        console.error('Error loading enterprises:', err);
      } finally {
        setLoadingEnterprises(false);
      }
    };

    loadEnterprises();
  }, [defaultEnterprise]);

  const loadLeads = useCallback(async () => {
    if (!selectedEnterprise) return;

    const controller = new AbortController();
    const previousController = currentLoadControllerRef.current;

    // Cancel any in-flight request so the latest refresh always wins
    if (previousController) {
      previousController.abort();
    }
    currentLoadControllerRef.current = controller;

    setError('');
    try {
      if (isInitialLoad) {
        setLoadingLeads(true);
      }

      const options = {
        page,
        size,
        sortBy,
        sortDirection,
        ...(bodyTypeFilter && { bodyTypeFilter }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(searchQuery && searchQuery.trim() && { searchQuery: searchQuery.trim() }),
        signal: controller.signal
      };

      const pageData = await getLeadsByEnterprise(selectedEnterprise, selectedStoreFilter, options);

      if (currentLoadControllerRef.current !== controller) {
        return;
      }

      const newLeads = pageData?.content || [];
      setLeads((prevLeads) => {
        const leadsChanged =
          prevLeads.length !== newLeads.length ||
          !prevLeads.every((prevLead, idx) => {
            const newLead = newLeads[idx];
            return prevLead && newLead &&
              prevLead.id === newLead.id &&
              JSON.stringify(prevLead) === JSON.stringify(newLead);
          });

        return leadsChanged ? newLeads : prevLeads;
      });

      setTotalElements(pageData?.totalElements || 0);
      setTotalPages(pageData?.totalPages || 0);
    } catch (err) {
      if (currentLoadControllerRef.current !== controller) {
        return;
      }
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        console.log('GI leads request cancelled');
        return;
      }
      console.error('Error loading GI leads:', err);
      setError('Failed to load leads');
    } finally {
      if (currentLoadControllerRef.current === controller) {
        currentLoadControllerRef.current = null;
        setLoadingLeads(false);
        setIsInitialLoad(false);
      }
    }
  }, [selectedEnterprise, selectedStoreFilter, isInitialLoad, page, size, sortBy, sortDirection, bodyTypeFilter, startDate, endDate, searchQuery]);

  useEffect(() => {
    loadLeadsRef.current = loadLeads;
  }, [loadLeads]);
  useEffect(() => {
    loadLeadsRef.current?.();
  }, [selectedEnterprise, selectedStoreFilter, page, size, sortBy, sortDirection, bodyTypeFilter, startDate, endDate]);

  useEffect(() => {
    if (selectedEnterprise) {
      leadEventBus.connectEnterprise(selectedEnterprise);
    } else {
      leadEventBus.disconnect();
    }
  }, [selectedEnterprise]);
  useEffect(() => {
    const unsubscribe = leadEventBus.subscribe((event) => {
      if (!event || event.type !== 'NEW_MESSAGE' || !event.leadId) {
        return;
      }
      if (event.enterpriseIdFk !== selectedEnterpriseRef.current) {
        return;
      }
      if (pageRef.current === 0) {
        setLeads((prev) => {
          const idx = prev.findIndex((lead) => lead.id === event.leadId);
          if (idx === -1) {
            // Not on this page yet — the refetch will pull it in.
            return prev;
          }

          const bumped = {
            ...prev[idx],
            lastReceivedMessageAt: event.lastActivity || prev[idx].lastReceivedMessageAt,
            lastActivity: event.lastActivity || prev[idx].lastActivity
          };

          if (idx === 0) {
            // Already on top — just refresh its activity timestamp.
            const sameSpot = [...prev];
            sameSpot[0] = bumped;
            return sameSpot;
          }

          const next = [...prev];
          next.splice(idx, 1);
          next.unshift(bumped);
          return next;
        });
      }

      loadLeadsRef.current?.();

      if (openLeadIdsRef.current.includes(event.leadId)) {
        return;
      }

      setUnreadLeadIds((prev) => {
        if (prev.has(event.leadId)) return prev;
        const next = new Set(prev);
        next.add(event.leadId);
        return next;
      });
    });

    return unsubscribe;
  }, []);

  const handleEnterpriseChange = async (enterpriseId) => {
    setSelectedEnterprise(enterpriseId);
    setSelectedStoreFilter(null);
    setOpenLeadIds([]);
    setPage(0);
    setStores([]);
    setIsInitialLoad(true);

    if (!enterpriseId) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/stores/enterprise/${enterpriseId}`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        setStores(await response.json());
      }
    } catch (err) {
      console.error('Error loading stores for enterprise:', err);
    }
  };

  const handleStoreFilterChange = (storeId) => {
    setSelectedStoreFilter(storeId);
    setPage(0);
    setIsInitialLoad(true);
  };

  const handleLeadSelect = useCallback((lead) => {
    openLead(lead);
    setFocusedLeadId(lead.id);
    setUnreadLeadIds((prev) => {
      if (!prev.has(lead.id)) return prev;
      const next = new Set(prev);
      next.delete(lead.id);
      return next;
    });
  }, [openLead, setUnreadLeadIds]);

  const handlePageChange = useCallback((newPage) => setPage(newPage), []);
  const handlePageSizeChange = useCallback((newSize) => { setSize(newSize); setPage(0); }, []);
  const handleSortChange = useCallback((newSortBy, newSortDirection) => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
    setPage(0);
  }, []);
  const handleBodyTypeFilterChange = useCallback((newBodyTypeFilter) => {
    setBodyTypeFilter(newBodyTypeFilter);
    setPage(0);
  }, []);
  const handleDateRangeChange = useCallback((newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      if (page !== 0) {
        setPage(0);
      } else {
        await loadLeads();
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [loadLeads, page]);

  const handleMessageSent = useCallback(() => {}, []);

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead and all related data? This action cannot be undone.')) {
      return;
    }

    setError('');
    try {
      setDeletingLeadId(leadId);

      const leadToDelete = leads.find((lead) => lead.id === leadId);
      if (
        leadToDelete?.appointment &&
        (leadToDelete.appointment.status === 'BOOKED' || leadToDelete.appointment.status === 'RESCHEDULED')
      ) {
        setError('');
        try {
          await fetch(`${getApiBaseUrl()}/appointments/${leadId}/cancel`, {
            method: 'DELETE',
            headers: authService.getAuthHeaders()
          });
        } catch (cancelError) {
          console.warn(cancelError);
        }
      }

      await deleteLead(leadId);

      closeLead(leadId);

      await loadLeads();
    } catch (err) {
      console.error('Error deleting lead:', err);
      setError('Failed to delete lead');
    } finally {
      setDeletingLeadId(null);
    }
  };

  const handleCancelAppointment = async (leadId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    setCancellingAppointmentId(leadId);

    setError('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/appointments/${leadId}/cancel`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });

      if (response.ok) {
        await loadLeads();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to cancel appointment');
      }
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      setError('Failed to cancel appointment');
    } finally {
      setCancellingAppointmentId(null);
    }
  };

  const storeFilterOptions = useMemo(
    () => stores.map((store) => ({ id: store.id, name: store.name, storeId: store.storeId, timeZone: store.timeZone })),
    [stores]
  );

  return (
    <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">

      {/* TOP BAR */}
      <div className="flex items-center gap-2 sm:gap-3">
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            title="Open sidebar"
            aria-label="Open sidebar"
            aria-expanded={false}
            className="-ml-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-700 transition-colors duration-150 hover:bg-gray-100/70 hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300"
          >
            <PanelLeft size={17} strokeWidth={1.5} />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <AppHeader
            user={user}
            onLogout={onLogout}
            badgeCount={totalElements}
            badgeLabel="Leads"
          />
        </div>
      </div>

      {/* BODY */}
      <div className="flex flex-1 overflow-hidden relative">

        {/* Mobile backdrop - tap to close the sidebar drawer */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* LEFT SIDEBAR - Enterprise Selection */}
        <div
          className="bg-white border-r border-gray-200 flex-shrink-0 overflow-y-auto overflow-x-hidden transition-[width] duration-300 ease-in-out fixed md:relative inset-y-0 md:inset-y-auto left-0 md:left-auto top-0 md:top-auto z-40 md:z-auto shadow-2xl md:shadow-none"
          style={{ width: sidebarOpen ? 288 : 0, maxWidth: sidebarOpen ? '85vw' : undefined }}
        >
          <div className="p-4" style={{ width: 288, maxWidth: '85vw' }}>
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 flex items-center">
                  <Building className="h-4 w-4 mr-2" />
                  Enterprise
                </span>
                <button
                  onClick={() => setSidebarOpen(false)}
                  title="Close sidebar"
                  aria-label="Close sidebar"
                  className="-mr-1 flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600"
                >
                  <PanelLeft size={14} strokeWidth={1.5} />
                </button>
              </div>

              <div className="relative">
                <select
                  value={selectedEnterprise || ''}
                  onChange={(e) => handleEnterpriseChange(e.target.value ? Number(e.target.value) : null)}
                  disabled={loadingEnterprises}
                  className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg shadow-sm appearance-none bg-white"
                >
                  <option value="">Select Enterprise</option>
                  {enterprises.map((ent) => (
                    <option key={ent.id} value={ent.id}>
                      {ent.name}
                    </option>
                  ))}
                </select>
              </div>

              <p className="text-xs text-gray-500">
                Shows leads from every store in this enterprise. Use the store
                filter above the table to narrow it down.
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT CONTENT - Leads Table */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6 min-w-0">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}

          {!selectedEnterprise ? (
            <div className="flex items-center justify-center py-16 text-center">
              <div>
                <Building size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select an Enterprise</h3>
                <p className="text-gray-600">Choose an enterprise on the left to view leads across all its stores.</p>
              </div>
            </div>
          ) : loadingLeads ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Loading leads...</span>
            </div>
          ) : (
            <LeadsTable
              leads={leads}
              onLeadSelect={handleLeadSelect}
              selectedLeadIds={openLeadIds}
              page={page}
              size={size}
              totalElements={totalElements}
              totalPages={totalPages}
              sortBy={sortBy}
              sortDirection={sortDirection}
              bodyTypeFilter={bodyTypeFilter}
              startDate={startDate}
              endDate={endDate}
              searchQuery={searchQuery}
              unreadLeadIds={unreadLeadIds}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onSortChange={handleSortChange}
              onBodyTypeFilterChange={handleBodyTypeFilterChange}
              onDateRangeChange={handleDateRangeChange}
              onSearchQueryChange={setSearchQuery}
              onRefresh={handleRefresh}
              isLoading={isRefreshing}
              showStoreColumn
              storeFilterOptions={storeFilterOptions}
              selectedStoreFilter={selectedStoreFilter}
              onStoreFilterChange={handleStoreFilterChange}
            />
          )}
        </div>

      </div>

      {openLeads.map((lead, index) => (
        <ChatWidget
          key={lead.id}
          selectedLead={lead}
          selectedLeadId={lead.id}
          storeTimeZone={lead.timeZone}
          rightOffsetPx={widgetRightOffset(index)}
          isFocused={focusedLeadId === lead.id}
          onFocusWidget={setFocusedLeadId}
          onWidthChange={handleWidgetWidthChange}
          onClose={() => closeLead(lead.id)}
          scrollToBottomOnLoad={scrollToBottomOnLoad && lead.id === lastOpenedLeadId}
          onMessageSent={handleMessageSent}
          onDeleteLead={handleDeleteLead}
          deletingLeadId={deletingLeadId}
          onCancelAppointment={handleCancelAppointment}
          user={user}
        />
      ))}

    </div>
  );
};

export default GIDashboard;
